import { Component, ElementRef, Inject, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatStepperModule } from '@angular/material/stepper';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatDialogRef } from '@angular/material/dialog';
import { interval, Subscription } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MessageBirdService } from 'src/app/core/servicie/messageBird.service';
import { VerifyNumberReminderModalComponent } from '../verify-number-reminder-modal/verify-number-reminder-modal.component';

@Component({
  selector: 'app-otp-validation',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatStepperModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './otp-validation.component.html',
  styleUrls: ['./otp-validation.component.scss']
})

export class OtpValidationComponent {
  firstFormGroup: FormGroup;
  secondFormGroup: FormGroup;
  otpInputs = [0, 1, 2, 3];
  attemptedVerification = false;
  errorMessage: string | null = null;
  timeLeft = 90;
  minutes = Math.floor(this.timeLeft / 60);
  seconds = this.timeLeft % 60;
  private countdownSubscription: Subscription | null = null;
  showAttemptExceededMessage = false;
  isLoading = false;

  @ViewChildren('otpInput') otpInputElements!: QueryList<ElementRef>;

  constructor(
    private _formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<OtpValidationComponent>,
    private dialog: MatDialog,
    private messageService: MessageBirdService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    const initialPhoneNumber = data.phoneNumber;
    this.firstFormGroup = this._formBuilder.group({
      phoneNumber: [
        initialPhoneNumber,
        [Validators.required, Validators.pattern(/^[0-9]{10}$/)]
      ]
    });
    this.secondFormGroup = this._formBuilder.group({
      otp0: ['', [Validators.required, Validators.pattern(/^[0-9]$/)]],
      otp1: ['', [Validators.required, Validators.pattern(/^[0-9]$/)]],
      otp2: ['', [Validators.required, Validators.pattern(/^[0-9]$/)]],
      otp3: ['', [Validators.required, Validators.pattern(/^[0-9]$/)]]
    });
  }

  isFieldValidFirst(field: string) {
    const control = this.firstFormGroup.get(field);
    return control && control.valid;
  }

  moveToNext(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    if (index === 0 && input.value.length > 1) {
      const otpCode = input.value.slice(0, 4);

      this.secondFormGroup.patchValue({
        otp0: otpCode[0] || '',
        otp1: otpCode[1] || '',
        otp2: otpCode[2] || '',
        otp3: otpCode[3] || '',
      });

      const otpInputsArray = this.otpInputElements.toArray();
      otpInputsArray[0].nativeElement.value = otpCode[0] || '';
      otpInputsArray[1].nativeElement.value = otpCode[1] || '';
      otpInputsArray[2].nativeElement.value = otpCode[2] || '';
      otpInputsArray[3].nativeElement.value = otpCode[3] || '';
      otpInputsArray[1]?.nativeElement.focus();
      return;
    }

    if (input.value.length >= input.maxLength) {
      const nextInput = this.otpInputElements.toArray()[index + 1];
      if (nextInput) {
        nextInput.nativeElement.focus();
      }
    }
  }

  handleInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    if (index === 0 && input.value.length > 1) {
      const otpCode = input.value.slice(0, 4);

      this.secondFormGroup.patchValue({
        otp0: otpCode[0] || '',
        otp1: otpCode[1] || '',
        otp2: otpCode[2] || '',
        otp3: otpCode[3] || '',
      });

      const otpInputsArray = this.otpInputElements.toArray();
      otpInputsArray[0].nativeElement.value = otpCode[0] || '';
      otpInputsArray[1].nativeElement.value = otpCode[1] || '';
      otpInputsArray[2].nativeElement.value = otpCode[2] || '';
      otpInputsArray[3].nativeElement.value = otpCode[3] || '';

      otpInputsArray[3]?.nativeElement.focus();
      return;
    }

    if (input.value.length === 1 && index < 3) {
      const nextInput = this.otpInputElements.toArray()[index + 1];
      if (nextInput) {
        nextInput.nativeElement.focus();
      }
    }

    if (index > 0 && input.value.length > 1) {
      input.value = input.value.slice(0, 1);
    }
  }


  handleBackspace(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Backspace') {
      if (input.value.length > 0) {
        input.value = '';
        this.secondFormGroup.get(`otp${index}`)?.setValue('');
      }
      else if (input.value.length === 0 && index > 0) {
        const prevInput = this.otpInputElements.toArray()[index - 1];
        if (prevInput) {
          prevInput.nativeElement.focus();
          prevInput.nativeElement.select();
        }
      }
    }
  }

  startCountdown(): void {
    this.timeLeft = 90;
    this.countdownSubscription = interval(1000).subscribe(() => {
      this.timeLeft--;
      this.minutes = Math.floor(this.timeLeft / 60);
      this.seconds = this.timeLeft % 60;
      if (this.timeLeft === 0 && this.countdownSubscription) {
        this.countdownSubscription.unsubscribe();
      }
    });
  }


  async sendCodeWhatsapp(stepper: any): Promise<void> {
    if (this.firstFormGroup.valid) {
      this.isLoading = true;
      this.clearMessages();
      const phoneNumber = this.firstFormGroup.get('phoneNumber')?.value;
      this.messageService.sendOtpCodewhatsapp(phoneNumber).subscribe({
        next: async (response) => {
          stepper.next();
          this.startCountdown();
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          if (error.status === 500 && error.error?.message === 'NumberAttemptsPassed') {
            this.showAttemptExceededMessage = true;
          }
        },
      });
    }
  }

  ngOnDestroy(): void {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
    }
  }


  verifyCode(stepper: any): void {
    this.attemptedVerification = true;
    if (this.secondFormGroup.valid) {
      this.isLoading = true;
      const otpCode = `${this.secondFormGroup.get('otp0')?.value}${this.secondFormGroup.get('otp1')?.value}${this.secondFormGroup.get('otp2')?.value}${this.secondFormGroup.get('otp3')?.value}`;

      this.messageService.verifyOtpCode(otpCode).subscribe({
        next: async (response: { success: boolean }) => {
          this.isLoading = false;
          if (response.success) {
            this.dialogRef.close(true);
            this.data.submitForm(this.data.group,true);
          } else {
            this.errorMessage = 'Código incorrecto. Por favor, intenta de nuevo.';
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.stopCountdown();
          console.log(error);
          if (error.message  === 'NumberAttemptsPassed') {
            stepper.reset();
            this.clearStepTwoErrors(true);
          } else {
            this.errorMessage = 'Hubo un error al verificar el código. Intenta de nuevo.';
          }
          console.log(this.errorMessage);
        }
      });
    }
  }

  clearStepTwoErrors(attempts=false): void {
    this.secondFormGroup.reset();
    this.errorMessage = null;
    this.attemptedVerification = false;
    this.showAttemptExceededMessage = false;
    if(attempts)
      this.errorMessage = 'Numero de intentos superados. Intenta de nuevo.';
  }

  stopCountdown(): void {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
    }
    this.timeLeft = 0;
  }

  closeModal(): void {
    const phoneNumber = this.firstFormGroup.get('phoneNumber')?.value;
  
    const completeData: any = {
      phoneNumber:phoneNumber, 
      country:this.data.country,
      group:this.data.group,
      submitForm: this.data.submitForm
    };
  
    this.dialogRef.close(false);
    this.dialog.open(VerifyNumberReminderModalComponent, {
      data: completeData
    });
  }  
  

  clearMessages(): void {
    this.errorMessage = null;
    this.showAttemptExceededMessage = false;
    this.attemptedVerification = false;
    this.secondFormGroup.reset();
  }
}

interface OTPCredentialOptions {
  transport: string[];
}

interface CredentialRequestOptionsExtended extends CredentialRequestOptions {
  otp?: OTPCredentialOptions;
}

interface OTPCredential extends Credential {
  code: string;
}

